import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import * as Yup from "yup";
import { toast } from "react-toastify";
import actionsHandler from "@/store/actions/actionHandler";
import DynamicForm from "@/components/common/DynamicForm";
import { InputFieldType } from "@/constants/enums/input-field-type.enum";
import { DynamicFormField } from "@/models/dynamic-form.model";
import { OnboardingDocumentMasterType } from "@/constants/enums/onboarding-document-master-types.enum";

export interface InputFields {
  id?: number;
  auditor_company_name?: string | null;
  auditor_email?: string | null;
  auditor_phone?: string | null;
  auditor_opinion?: string | null;
  directors_names?: string | null;
  shareholding_breakdown?: string | null;
  financial_year_end?: string | null;
  audit_comments?: string | null;
  notification_date?: string | null;
  income_tax_number?: string | null;
  vat_number?: string | null;
  customs_duties_number?: string | null;
  bank_account_number?: string | null;
  cash_flow_calculation?: string | null;
  is_document_bank_stamped?: boolean;
  is_account_in_company_name?: boolean;
  avs_check_completed?: boolean;
  company_name?: string | null;
  company_registration_number?: string | null;
  company_address?: string | null;
  year_established?: string | null;
  company_comments?: string | null;
  company_ubo?: string | null;
  legal_shareholders?: string | null;
  shareholders_info?: Record<string, any>;
  directors_contact_numbers?: string | null;
  directors_email_addresses?: string | null;
  directors_comments?: string | null;
  is_reseller?: boolean;
  dme_number?: string | null;
  additional_comments?: string | null;
  next_notification_date?: string | null;
  broker_name?: string | null;
  broker_contact?: string | null;
  insurance_start_date?: string | null;
  insurance_end_date?: string | null;
  policy_insured?: string | null;
  policy_insured_value?: string | null;
  insurance_comments?: string | null;
  bank?: string | null;
}
interface OnboardingInputFieldsProps {
  document_master: OnboardingDocumentMasterType;
  input_fields?: InputFields;
  inputFieldCustomOptions?: Record<string, any>;
  readOnly?: boolean;
  isFinOb1?: boolean;
  user_id?: number;
  onSave?: () => void;
}

export const enumFields = {
  [OnboardingDocumentMasterType.finOb1]: [
    {
      label: "Registered Company Name",
      name: "register_company_name",
      required: true,
      type: InputFieldType.TEXT,
      placeholder: "Enter registered company name",
      validation: Yup.string(),
    },
    {
      label: "Trading As",
      name: "trading_as",
      required: true,
      type: InputFieldType.TEXT,
      placeholder: "Enter trading as",
      validation: Yup.string(),
    },
    {
      label: "Commodity Type",
      name: "commodity_types",
      type: InputFieldType.SELECT,
      required: true,
      options: [
        { id: 1, label: "Option 1", value: 1 },
        { id: 2, label: "Option 2", value: 2 },
      ],
      validation: Yup.number(),
    },
    {
      label: "Facility Required",
      name: "facility_required",
      type: InputFieldType.CHECKBOX,
      validation: Yup.boolean(),
    },
    {
      label: "Facility Requested Amount",
      name: "facility_requested_amount",
      required: true,
      type: InputFieldType.NUMBER,
      placeholder: "Enter facility requested amount",
      validation: Yup.number(),
      dependsOn: {
        field: "facility_required",
        value: true,
      },
    },
  ],
  [OnboardingDocumentMasterType.finOb2]: [
    {
      label: "Auditor Company Name",
      name: "auditor_company_name",
      type: InputFieldType.TEXT,
      required: true,
      placeholder: "Enter company name",
      validation: Yup.string(),
    },
    {
      label: "Auditor Email",
      name: "auditor_email",
      type: InputFieldType.EMAIL,
      required: true,
      placeholder: "Enter email address",
      validation: Yup.string().email("Invalid email"),
    },
    {
      label: "Auditor Phone Number",
      name: "auditor_phone",
      type: InputFieldType.TEL,
      required: true,
      placeholder: "Enter phone number",
      validation: Yup.string()
        .required("Auditor Phone number is required")
        .min(9, "Phone number must be at least 9 digits")
        .max(10, "Phone number must be at most 10 digits")
        .matches(/^\d{1,10}$/, "Invalid phone number"),
    },
    {
      label: "Auditor Opinion",
      name: "auditor_opinion",
      type: InputFieldType.TEXTAREA,
      required: true,
      placeholder: "Enter auditor opinion",
      validation: Yup.string(),
    },
    {
      label: "Directors Names",
      name: "directors_names",
      type: InputFieldType.TEXT,
      required: true,
      placeholder: "Enter directors names",
      validation: Yup.string(),
    },
    {
      label: "Shareholding Breakdown",
      name: "shareholding_breakdown",
      type: InputFieldType.TEXTAREA,
      required: true,
      placeholder: "Enter shareholding breakdown",
      validation: Yup.string(),
    },
    {
      label: "Financial Year End",
      name: "financial_year_end",
      type: InputFieldType.DATE,
      required: true,
      validation: Yup.string(),
    },
    {
      label: "Audit Comments",
      name: "audit_comments",
      type: InputFieldType.TEXTAREA,
      placeholder: "Enter audit comments",
      validation: Yup.string(),
    },
  ],
  [OnboardingDocumentMasterType.finOb3]: [
    {
      label: "Comments",
      name: "additional_comments",
      type: InputFieldType.TEXTAREA,
      placeholder: "Enter a comments",
      validation: Yup.string(),
    },
  ],
  [OnboardingDocumentMasterType.finOb4]: [
    {
      label: "Comments",
      name: "additional_comments",
      type: InputFieldType.TEXTAREA,
      placeholder: "Enter a comments",
      validation: Yup.string(),
    },
  ],
  [OnboardingDocumentMasterType.finOb5]: [
    {
      label: "Comments",
      name: "additional_comments",
      type: InputFieldType.TEXTAREA,
      placeholder: "Enter a comments",
      validation: Yup.string(),
    },
  ],
  [OnboardingDocumentMasterType.finOb6]: [
    {
      label: "Comments",
      name: "additional_comments",
      type: InputFieldType.TEXTAREA,
      placeholder: "Enter a comments",
      validation: Yup.string(),
    },
  ],
  [OnboardingDocumentMasterType.finOb7]: [
    {
      label: "Notification Date",
      name: "notification_date",
      type: InputFieldType.DATE,
      required: true,
      validation: Yup.string(),
    },
    {
      label: "Comments",
      name: "additional_comments",
      type: InputFieldType.TEXTAREA,
      placeholder: "Enter a comments",
      validation: Yup.string(),
    },
  ],
  [OnboardingDocumentMasterType.finOb8]: [
    {
      label: "Comments",
      name: "additional_comments",
      type: InputFieldType.TEXTAREA,
      placeholder: "Enter a comments",
      validation: Yup.string(),
    },
  ],
  [OnboardingDocumentMasterType.finOb9]: [
    {
      label: "Comments",
      name: "additional_comments",
      type: InputFieldType.TEXTAREA,
      placeholder: "Enter a comments",
      validation: Yup.string(),
    },
  ],
  [OnboardingDocumentMasterType.finOb10]: [
    {
      label: "Income Tax Number",
      name: "income_tax_number",
      type: InputFieldType.TEXT,
      required: true,
      placeholder: "Enter income tax number",
      validation: Yup.string(),
    },
    {
      label: "VAT Number",
      name: "vat_number",
      type: InputFieldType.TEXT,
      required: true,
      placeholder: "Enter VAT number",
      validation: Yup.string(),
    },
    {
      label: "Customs Duties Number",
      name: "customs_duties_number",
      type: InputFieldType.TEXT,
      placeholder: "Enter customs duties number",
      validation: Yup.string(),
    },
    {
      label: "Comments",
      name: "additional_comments",
      type: InputFieldType.TEXTAREA,
      placeholder: "Enter a comments",
      validation: Yup.string(),
    },
  ],
  [OnboardingDocumentMasterType.finOb11]: [
    {
      label: "Bank",
      name: "bank",
      type: InputFieldType.SELECT,
      required: true,
      options: [
        { id: 1, label: "Bank 1", value: 1 },
        { id: 2, label: "Bank 2", value: 2 },
      ],
      validation: Yup.number().required("Bank is required"),
    },
    {
      label: "Bank Account Number",
      name: "bank_account_number",
      type: InputFieldType.TEXT,
      required: true,
      placeholder: "Enter bank account number",
      validation: Yup.string(),
    },
    {
      label: "Comments",
      name: "additional_comments",
      type: InputFieldType.TEXTAREA,
      placeholder: "Enter a comments",
      validation: Yup.string(),
    },
  ],
  [OnboardingDocumentMasterType.lglOb1]: [
    {
      label: "Company Name",
      name: "company_name",
      type: InputFieldType.TEXT,
      required: true,
      placeholder: "Enter company name",
      validation: Yup.string(),
    },
    {
      label: "Company Registration Number",
      name: "company_registration_number",
      type: InputFieldType.TEXT,
      required: true,
      placeholder: "Enter registration number",
      validation: Yup.string(),
    },
    {
      label: "Company Established Year",
      name: "year_established",
      type: InputFieldType.NUMBER,
      required: true,
      placeholder: "Enter company established year",
      validation: Yup.number(),
    },
    {
      label: "Company Address",
      name: "company_address",
      type: InputFieldType.TEXT,
      required: true,
      placeholder: "Enter company address",
      validation: Yup.string(),
    },
    {
      label: "Comments",
      name: "company_comments",
      type: InputFieldType.TEXTAREA,
      placeholder: "Enter a comments",
      validation: Yup.string(),
    },
  ],
  [OnboardingDocumentMasterType.lglOb2]: [
    {
      label: "Comments",
      name: "additional_comments",
      type: InputFieldType.TEXTAREA,
      placeholder: "Enter a comments",
      validation: Yup.string(),
    },
  ],
  [OnboardingDocumentMasterType.lglOb3]: [
    {
      label: "Company UBO (Name & Surname)",
      name: "company_ubo",
      type: InputFieldType.TEXT,
      required: true,
      placeholder: "Enter company UBO",
      validation: Yup.string(),
    },
    {
      label: "Legal Shareholders (Name & Surname)",
      name: "legal_shareholders",
      type: InputFieldType.TEXT,
      required: true,
      placeholder: "Enter legal shareholders",
      validation: Yup.string(),
    },
    {
      label: "Shareholders",
      name: "shareholders_info",
      type: InputFieldType.ARRAY_OF_OBJECT,
      required: false,
      showAddButton: true,
      children: [
        {
          label: "Shareholder 1",
          name: "shareholder_1",
          type: InputFieldType.ARRAY_OF_OBJECT,
          required: false,
          children: [
            {
              label: "Shareholder Name",
              name: "shareholder_name",
              type: InputFieldType.TEXT,
              required: true,
              validation: Yup.string(),
            },
            {
              label: "Percentage",
              name: "percentage",
              type: InputFieldType.NUMBER,
              required: true,
              validation: Yup.number().min(0).max(100),
              flexed: true,
              gridBreakPoint: 4,
            },
            {
              label: "Date Issued",
              name: "date_issued",
              type: InputFieldType.DATE,
              required: true,
              validation: Yup.string(),
              flexed: true,
              gridBreakPoint: 8,
            },
          ],
        },
      ],
      validation: Yup.array().of(
        Yup.object().shape({
          shareholder_name: Yup.string().required(),
          percentage: Yup.number().required(),
          date_issued: Yup.string().required(),
        }),
      ),
    },
    {
      label: "Comments",
      name: "additional_comments",
      type: InputFieldType.TEXTAREA,
      placeholder: "Enter a comments",
      validation: Yup.string(),
    },
  ],
  [OnboardingDocumentMasterType.lglOb4]: [
    {
      label: "Comments",
      name: "additional_comments",
      type: InputFieldType.TEXTAREA,
      placeholder: "Enter a comments",
      validation: Yup.string(),
    },
  ],
  [OnboardingDocumentMasterType.lglOb5]: [
    {
      label: "Directors Names & Surnames",
      name: "directors_names",
      type: InputFieldType.TEXT,
      required: true,
      placeholder: "Enter directors names",
      validation: Yup.string(),
    },
    {
      label: "Directors Contact Numbers",
      name: "directors_contact_numbers",
      type: InputFieldType.TEL,
      required: true,
      placeholder: "Enter contact numbers",
      validation: Yup.string(),
    },
    {
      label: "Directors Email Addresses",
      name: "directors_email_addresses",
      type: InputFieldType.EMAIL,
      required: true,
      placeholder: "Enter email addresses",
      validation: Yup.string().email("Invalid email"),
    },
    {
      label: "Comments",
      name: "directors_comments",
      type: InputFieldType.TEXTAREA,
      placeholder: "Enter a comments",
      validation: Yup.string(),
    },
  ],
  [OnboardingDocumentMasterType.lglOb6]: [
    {
      label: "Reseller?",
      name: "is_reseller",
      type: InputFieldType.CHECKBOX,
      validation: Yup.boolean(),
    },
    {
      label: "DME Number",
      name: "dme_number",
      type: InputFieldType.TEXT,
      placeholder: "Enter DME number",
      validation: Yup.string(),
    },
    {
      label: "Comments",
      name: "additional_comments",
      type: InputFieldType.TEXTAREA,
      placeholder: "Enter a comments",
      validation: Yup.string(),
    },
  ],
  [OnboardingDocumentMasterType.lglOb7]: [
    {
      label: "Next Notification Date",
      name: "next_notification_date",
      type: InputFieldType.DATE,
      required: true,
      validation: Yup.string(),
    },
    {
      label: "Broker Name",
      name: "broker_name",
      type: InputFieldType.TEXT,
      required: true,
      placeholder: "Enter broker name",
      validation: Yup.string(),
    },
    {
      label: "Contact Details",
      name: "broker_contact",
      type: InputFieldType.TEXT,
      required: true,
      placeholder: "Enter contact details",
      validation: Yup.string(),
    },
    {
      label: "Start Date",
      name: "insurance_start_date",
      type: InputFieldType.DATE,
      required: true,
      validation: Yup.string(),
    },
    {
      label: "End Date",
      name: "insurance_end_date",
      type: InputFieldType.DATE,
      required: true,
      validation: Yup.string(),
    },
    {
      label: "Policy Insured",
      name: "policy_insured",
      type: InputFieldType.TEXT,
      required: true,
      placeholder: "Enter policy insured",
      validation: Yup.string(),
    },
    {
      label: "Policy Insured Value",
      name: "policy_insured_value",
      type: InputFieldType.NUMBER,
      required: true,
      placeholder: "Enter policy insured value",
      validation: Yup.number(),
    },
    {
      label: "Comments",
      name: "insurance_comments",
      type: InputFieldType.TEXTAREA,
      placeholder: "Enter insurance comments",
      validation: Yup.string(),
    },
  ],
};

const OnboardingInputFields: React.FC<OnboardingInputFieldsProps> = ({
  document_master,
  input_fields = {},
  inputFieldCustomOptions = {},
  readOnly = false,
  isFinOb1 = false,
  user_id,
  onSave,
}) => {
  const { id } = useParams();
  const dispatch = useDispatch<AppThunkDispatch>();

  const fields: DynamicFormField[] = useMemo(() => {
    let data = [...(enumFields[document_master] || [])];
    if (inputFieldCustomOptions) {
      data = data.map((field: any) => {
        if (inputFieldCustomOptions[field.name] !== undefined) {
          field["options"] = inputFieldCustomOptions[field.name];
        }
        return field;
      });
    }
    return data;
  }, [document_master, inputFieldCustomOptions]);

  const handleSubmit = async (values: any) => {
    try {
      const { authenticatedCall } = actionsHandler(
        isFinOb1
          ? "updateOnboardingDocumentFinOb1InputFields"
          : "updateOnboardingDocumentInputFields",
      );
      const response = await dispatch(
        authenticatedCall({
          method: "post",
          payload: { ...values, user_id },
          customParams: !isFinOb1 ? [`${id}/`] : undefined,
        }),
      );
      if (response.ok) {
        toast.success(response.data.message);
        onSave?.();
      }
    } catch (error) {
      console.error("Error updating fields:", error);
    }
  };

  return (
    <DynamicForm
      fields={fields}
      onSubmit={handleSubmit}
      submitButtonText="Save"
      initialValues={input_fields}
      readOnly={readOnly}
    />
  );
};

export default OnboardingInputFields;
